/**
 * Normalizes a string for accent-insensitive comparison
 * - Removes diacritics (accents)
 * - Converts to lowercase
 * - Normalizes Greek final sigma (ς) to regular sigma (σ)
 *
 * @param str - The string to normalize
 * @returns Normalized string for comparison
 *
 * @example
 * normalizeString('Μαρία') // 'μαρια'
 * normalizeString('Νίκος') // 'νικοσ'
 * normalizeString('café') // 'cafe'
 */
export const normalizeString = (str: string): string => {
  if (!str) return '';

  return (
    str
      .toLowerCase()
      // Normalize Unicode characters and remove diacritics (accents)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Normalize Greek final sigma (ς) to regular sigma (σ)
      .replace(/ς/g, 'σ')
  );
};

/**
 * Checks if a normalized string contains a normalized search term
 *
 * @param text - The text to search in
 * @param searchTerm - The term to search for
 * @returns True if the normalized text contains the normalized search term
 *
 * @example
 * containsNormalized('Μαρία', 'μαρια') // true
 * containsNormalized('Νίκος', 'νικοσ') // true
 * containsNormalized('Café', 'cafe') // true
 */
export const containsNormalized = (text: string, searchTerm: string): boolean => {
  return normalizeString(text).includes(normalizeString(searchTerm));
};
